/**
 * EphemeralCrossingInspector — Iter 37 Utility Intelligence V1
 * (renamed Iter 63.1 to disambiguate from the persistent
 * `components/object/ConflictInspector.jsx`, which represents a
 * crossing that's been PROMOTED to a tracked operational record).
 *
 * The "Tap conflict → see details" surface. Pure information panel.
 *
 * Per directive:
 *   - Utility A · Utility B
 *   - Utility Types
 *   - Approximate Location
 *   - Available Metadata
 * NEVER:
 *   - "Engineering violation"
 *   - "Design error"
 *   - Suggested corrections
 *
 * Renders as a compact bottom-right card (drawing-first — never modal,
 * never blocks the canvas). Esc + close button dismiss. Stays clear of
 * the LocateControl cluster by sitting in the bottom-LEFT area when the
 * Inspector panel (right side) is open.
 */
import React, { useEffect } from "react";
import { X, AlertTriangle, MapPin, Info } from "lucide-react";
import { SEVERITY_TONE, LAYER_LABEL } from "@/lib/utilityIntel";


const STRUCTURE_TYPE_LABEL = {
  storm_manhole: "Manhole",
  inlet: "Inlet",
  junction_box: "Junction Box",
  pull_box: "Pull Box",
  valve: "Valve",
  headwall: "Headwall",
};


function elevLabel(s) {
  // Most operationally meaningful elevation snapshot for a point structure.
  const bits = [];
  if (s.invert_out != null) bits.push(`inv out ${s.invert_out}'`);
  if (s.invert_in != null) bits.push(`inv in ${s.invert_in}'`);
  if (s.rim_elevation != null) bits.push(`rim ${s.rim_elevation}'`);
  return bits.join(" · ") || null;
}


function StructureCard({ s, side }) {
  const layerLabel = LAYER_LABEL[s.layer_type] || s.layer_type;
  const typeLabel = STRUCTURE_TYPE_LABEL[s.structure_type] || s.structure_type;
  const elev = elevLabel(s);
  return (
    <div className="flex-1 min-w-0" data-testid={`conflict-utility-${side}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">
        Utility {side.toUpperCase()}
      </div>
      <div className="font-mono text-sm font-bold text-[var(--fop-text)] leading-tight truncate" title={s.structure_label}>
        {s.structure_label}
      </div>
      <div className="text-[11px] text-[var(--fop-text-2)] mt-0.5 truncate">
        {layerLabel} · {typeLabel}
      </div>
      {s.pipe_size && (
        <div className="text-[10px] text-[var(--fop-text-3)] mt-0.5 truncate">{s.pipe_size}</div>
      )}
      {elev && (
        <div className="text-[10px] font-mono text-[var(--fop-text-3)] mt-0.5 truncate">{elev}</div>
      )}
    </div>
  );
}


export default function EphemeralCrossingInspector({ crossing, onClose, onFocusStructure, inspectorOpen }) {
  // Esc dismiss.
  useEffect(() => {
    if (!crossing) return;
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [crossing, onClose]);

  if (!crossing) return null;

  const tone = SEVERITY_TONE[crossing.severity] || SEVERITY_TONE.watch;
  const clr = crossing.clearance_ft;
  // Position — bottom-left when the right-side Inspector is open, else
  // bottom-right. Keeps it from colliding with LocateControl (br) or the
  // Inspector panel.
  const positionClass = inspectorOpen
    ? "left-4 bottom-4"
    : "left-4 bottom-4"; // V1 always bottom-left so it never collides with Locate cluster
  const approxLocation = `nx ${crossing.midpoint.nx.toFixed(3)} · ny ${crossing.midpoint.ny.toFixed(3)}`;

  return (
    <div
      className={`fixed ${positionClass} z-30 w-[340px] rounded-lg border bg-[var(--fop-surface)]/95 backdrop-blur shadow-2xl`}
      style={{ borderColor: tone.color }}
      data-testid="conflict-inspector"
      data-crossing-id={crossing.id}
      data-severity={crossing.severity}
    >
      {/* Header */}
      <div className="px-3 py-2 flex items-center gap-2 border-b border-[var(--fop-border)]" style={{ background: tone.soft }}>
        <AlertTriangle size={14} style={{ color: tone.color }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: tone.color }}>
            Potential {tone.label}
          </div>
          <div className="text-[11px] text-[var(--fop-text-2)] truncate" title={crossing.label_pair}>
            {crossing.label_pair}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] transition-colors"
          data-testid="conflict-inspector-close"
          aria-label="Close"
          title="Close (Esc)"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body — Utility A | Utility B */}
      <div className="px-3 py-3 flex gap-3">
        <StructureCard s={crossing.a} side="a" />
        <div className="w-px self-stretch bg-[var(--fop-border)]" />
        <StructureCard s={crossing.b} side="b" />
      </div>

      {/* Metadata strip */}
      <div className="px-3 py-2 border-t border-[var(--fop-border)] space-y-1.5">
        <div className="flex items-center gap-2 text-[10px]">
          <MapPin size={11} className="text-[var(--fop-text-3)]" />
          <span className="text-[var(--fop-text-3)] font-mono uppercase tracking-wider">Approx</span>
          <span className="font-mono text-[var(--fop-text-2)]" data-testid="conflict-approx-location">
            {approxLocation}
          </span>
        </div>
        {clr != null && (
          <div className="flex items-center gap-2 text-[10px]">
            <Info size={11} className="text-[var(--fop-text-3)]" />
            <span className="text-[var(--fop-text-3)] font-mono uppercase tracking-wider">Clearance</span>
            <span className="font-mono text-[var(--fop-text-2)]" data-testid="conflict-clearance">
              ~{clr.toFixed(2)} ft (estimated)
            </span>
          </div>
        )}
        <div className="text-[9px] text-[var(--fop-text-3)] italic leading-snug pt-0.5">
          Operational awareness only. Not engineering certification.
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-3 py-2 border-t border-[var(--fop-border)] flex items-center gap-2">
        <button
          type="button"
          onClick={() => onFocusStructure && onFocusStructure(crossing.a)}
          className="flex-1 px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)] transition-colors truncate"
          data-testid="conflict-focus-a"
          title={`Inspect ${crossing.a.structure_label}`}
        >
          Inspect {crossing.a.structure_label}
        </button>
        <button
          type="button"
          onClick={() => onFocusStructure && onFocusStructure(crossing.b)}
          className="flex-1 px-2 py-1 rounded text-[11px] font-mono uppercase tracking-wider border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:border-[var(--fop-primary)] hover:text-[var(--fop-primary)] transition-colors truncate"
          data-testid="conflict-focus-b"
          title={`Inspect ${crossing.b.structure_label}`}
        >
          Inspect {crossing.b.structure_label}
        </button>
      </div>
    </div>
  );
}
